import React, { useEffect, useMemo, useState } from "react";
import ttAPI from '../../api/ttAPI';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import {
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";

import config from '../../config'
import CustomerNotesReviewDialog from './CustomerNotesReviewDialog';

const monthYearToDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`01 ${value}`);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const isAfterLastVisit = (expiry, lastSiteVisit) => {
  const expiryDate = monthYearToDate(expiry);
  const lastVisitDate = monthYearToDate(lastSiteVisit);

  if (!expiryDate || !lastVisitDate) return true;
  return expiryDate > lastVisitDate;
};

const MultiSiteEstimateDialog = ({ open, onClose, siteId, customerId, customerNotes, bookingInstructions }) => {
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [siteSelections, setSiteSelections] = useState({});
  const [siteDataCache, setSiteDataCache] = useState({});
  const [openCustomerNotesReview, setOpenCustomerNotesReview] = useState(false);

  useEffect(() => {
    if (!open || !customerId) {
      return;
    }

    let mounted = true;

    const fetchSites = async () => {
      setLoadingSites(true);
      try {
        const response = await ttAPI.getCustomerSites(customerId);
        const customerSites = response || [];

        if (!mounted) return;

        setSites(customerSites);

        const initialState = {};
        customerSites.forEach((site) => {
          initialState[site.id] = {
            site_id: site.id,
            site_name: site.name,
            included: true,
            expanded: site.id === siteId,
            months_selected: [],
            total_assets_selected: 0,
            total_assets_available: 0,
            last_site_visit: '',
          };
        });

        setSiteSelections(initialState);
        setSiteDataCache({});

        const defaultExpandedSite = customerSites.find(site => site.id === siteId);
        if (defaultExpandedSite) {
          fetchSiteData(defaultExpandedSite.id, initialState);
        }
      } catch (e) {
        console.error("Failed to load customer sites", e);
      } finally {
        if (mounted) {
          setLoadingSites(false);
        }
      }
    };

    fetchSites();

    return () => {
      mounted = false;
    };
  }, [open, customerId, siteId]);

  const fetchSiteData = async (targetSiteId, latestSelections = null) => {
    if (!targetSiteId) return;

    const existingCache = siteDataCache[targetSiteId];
    if (existingCache?.loading || existingCache?.loaded) {
      return;
    }

    setSiteDataCache((prev) => ({
      ...prev,
      [targetSiteId]: {
        ...(prev[targetSiteId] || {}),
        loading: true,
        loaded: false,
        rows: [],
      },
    }));

    try {
      const [summary, lastJobOnSite] = await Promise.all([
        ttAPI.getSiteAssetsExpiryByMonthSummary(targetSiteId),
        ttAPI.getLastJobOnSite(targetSiteId),
      ]);

      const rows = summary || [];
      const lastSiteVisit = lastJobOnSite?.last_site_visit || '';

      const filteredRows = rows.filter((row) =>
        isAfterLastVisit(row.expiry, lastSiteVisit)
      );

      const totalAssetsAvailable = filteredRows.reduce(
        (sum, row) => sum + (row.assets || 0),
        0
      );

      const currentSelections = latestSelections || siteSelections;
      const existingSelection = currentSelections[targetSiteId];
      const alreadyHasSelection =
        existingSelection &&
        existingSelection.months_selected &&
        existingSelection.months_selected.length > 0;

      const defaultMonthsSelected = filteredRows.length > 0
        ? [filteredRows[0].expiry]
        : [];

      const defaultSelectedTotal = filteredRows.length > 0
        ? (filteredRows[0].assets || 0)
        : 0;

      setSiteDataCache((prev) => ({
        ...prev,
        [targetSiteId]: {
          loading: false,
          loaded: true,
          rows: filteredRows,
        },
      }));

      setSiteSelections((prev) => {
        const existing = prev[targetSiteId];
        if (!existing) return prev;

        return {
          ...prev,
          [targetSiteId]: {
            ...existing,
            last_site_visit: lastSiteVisit,
            total_assets_available: totalAssetsAvailable,
            months_selected: alreadyHasSelection
              ? existing.months_selected
              : defaultMonthsSelected,
            total_assets_selected: alreadyHasSelection
              ? existing.total_assets_selected
              : defaultSelectedTotal,
          },
        };
      });
    } catch (e) {
      console.error(`Failed to load site estimate data for site ${targetSiteId}`, e);

      setSiteDataCache((prev) => ({
        ...prev,
        [targetSiteId]: {
          loading: false,
          loaded: true,
          rows: [],
        },
      }));
    }
  };

  const handleSitePatch = (siteIdToPatch, patch) => {
    const isExpanding = patch.expanded === true && !siteSelections[siteIdToPatch]?.expanded;

    setSiteSelections((prev) => ({
      ...prev,
      [siteIdToPatch]: {
        ...prev[siteIdToPatch],
        ...patch,
      },
    }));

    if (isExpanding) {
      fetchSiteData(siteIdToPatch);
    }
  };

  const handleMonthToggle = (siteIdToPatch, expiry, checked) => {
    setSiteSelections((prev) => {
      const current = prev[siteIdToPatch];
      if (!current) return prev;

      let nextMonths = current.months_selected || [];

      if (checked) {
        if (!nextMonths.includes(expiry)) {
          nextMonths = [...nextMonths, expiry];
        }
      } else {
        nextMonths = nextMonths.filter((m) => m !== expiry);
      }

      const siteRows = siteDataCache[siteIdToPatch]?.rows || [];
      const nextTotal = siteRows
        .filter((row) => nextMonths.includes(row.expiry))
        .reduce((sum, row) => sum + (row.assets || 0), 0);

      return {
        ...prev,
        [siteIdToPatch]: {
          ...current,
          months_selected: nextMonths,
          total_assets_selected: nextTotal,
        },
      };
    });
  };

  const selectedSiteCount = useMemo(() => {
    return Object.values(siteSelections).filter(
      (site) => site.months_selected && site.months_selected.length > 0
    ).length;
  }, [siteSelections]);

  const totals = useMemo(() => {
    return Object.values(siteSelections).reduce(
      (acc, site) => {
        acc.assets += site.total_assets_selected || 0;
        return acc;
      },
      { assets: 0 }
    );
  }, [siteSelections]);

  const selectedPayload = useMemo(() => {
    return Object.values(siteSelections)
      .filter(
        (site) =>
          site.months_selected &&
          site.months_selected.length > 0
      )
      .map((site) => ({
        site_id: site.site_id,
        months_selected: site.months_selected,
      }));
  }, [siteSelections]);

  const handleGenerate = () => {
    if (!selectedPayload || selectedPayload.length === 0) return;

    setOpenCustomerNotesReview(true);
  };

  const generateEstimate = () => {
    if (!selectedPayload || selectedPayload.length === 0) return;

    const sitesParam = selectedPayload
      .map(s => {
        const months = s.months_selected.join(',');
        return `${s.site_id}:${months}`;
      })
      .join('|');

    const url = `${config.clientApi}/dashboard/multi_site_estimate?sites=${encodeURIComponent(sitesParam)}`;

    window.open(url, '_blank');
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="multi-site-estimate-dialog-title"
    >
      <DialogTitle id="multi-site-estimate-dialog-title">
        Multi-Site Estimate
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary">
          Select one or more expiry groups across customer sites.
        </Typography>

        <div style={{ marginTop: 8, marginBottom: 16 }}>
          <Typography variant="body2">
            {sites.length} site{sites.length === 1 ? '' : 's'} available, {selectedSiteCount} site{selectedSiteCount === 1 ? '' : 's'} selected, {totals.assets} assets selected
          </Typography>
        </div>

        <Divider style={{ marginBottom: 16 }} />

        {loadingSites ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
            <CircularProgress size={28} />
          </div>
        ) : sites.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No customer sites found.
          </Typography>
        ) : (
          sites.map((site) => {
            const siteState = siteSelections[site.id] || {};
            const siteCache = siteDataCache[site.id] || {};
            const rows = siteCache.rows || [];
            const isExpanded = !!siteState.expanded;
            const hasMonthsSelected = !!(siteState.months_selected && siteState.months_selected.length > 0);

            return (
              <Paper
                key={site.id}
                elevation={0}
                style={{
                  marginBottom: 12,
                  border: '1px solid rgba(0,0,0,0.12)',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
              <div
                onClick={() => handleSitePatch(site.id, { expanded: !isExpanded })}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  background: isExpanded ? 'rgba(0,0,0,0.02)' : 'white',
                  cursor: 'pointer',
                }}
                >
                <div
                  style={{
                    width: 32,
                    minWidth: 32,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  >
                  {hasMonthsSelected ? (
                    <CheckCircleIcon style={{ color: '#FFCA06' }} />
                  ) : null}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" noWrap>
                      {site.name}
                    </Typography>
                    {siteCache.loaded && (
                        <Typography variant="body2" color="textSecondary" noWrap>
                          {siteState.total_assets_selected || 0} selected
                          {siteState.total_assets_available ? ` / ${siteState.total_assets_available} total` : ''}
                          {siteState.last_site_visit ? ` • Last visited: ${siteState.last_site_visit}` : ''}
                        </Typography>
                      )}
                  </div>

                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSitePatch(site.id, { expanded: !isExpanded });
                    }}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandLessIcon style={{ transform: 'rotate(180deg)' }} />}
                  </IconButton>
                </div>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Divider />
                  <div style={{ padding: 16 }}>
                    {siteCache.loading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                        <CircularProgress size={24} />
                      </div>
                    ) : rows.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No future expiry groups found for this site.
                      </Typography>
                    ) : (
                      <>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '8px 4px' }}></th>
                              <th style={{ textAlign: 'left', padding: '8px 4px' }}>Expiry</th>
                              <th style={{ textAlign: 'center', padding: '8px 4px' }}>#Assets</th>
                              <th style={{ textAlign: 'center', padding: '8px 4px' }}>Test Period</th>
                              <th style={{ textAlign: 'center', padding: '8px 4px' }}>FE</th>
                              <th style={{ textAlign: 'center', padding: '8px 4px' }}>FA</th>
                              <th style={{ textAlign: 'center', padding: '8px 4px' }}>MED</th>
                              <th style={{ textAlign: 'center', padding: '8px 4px' }}>EVSE</th>
                              <th style={{ textAlign: 'center', padding: '8px 4px' }}>AED</th>
                              <th style={{ textAlign: 'center', padding: '8px 4px' }}>LG/RCD/3PH</th>
                              <th style={{ textAlign: 'center', padding: '8px 4px' }}>MW</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row) => {
                              const checked = (siteState.months_selected || []).includes(row.expiry);
                              const lgRcd3ph =
                                (row.three_phase_count || 0) +
                                (row.rcd_m_count || 0) +
                                (row.rcd_pb_count || 0) +
                                (row.lg_m_count || 0) +
                                (row.lg_pb_count || 0) +
                                (row.three_phase_rcd_count || 0);
                              const mw =
                                (row.microwave_count || 0) +
                                (row.microwave_leakage_count || 0);

                              return (
                                <tr key={`${site.id}-${row.expiry}`} style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                                  <td style={{ padding: '8px 4px' }}>
                                    <Checkbox
                                      checked={checked}
                                      onChange={(e) =>
                                        handleMonthToggle(site.id, row.expiry, e.target.checked)
                                      }
                                      color="primary"
                                    />
                                  </td>
                                  <td style={{ padding: '8px 4px' }}>{row.expiry}</td>
                                  <td style={{ textAlign: 'center', padding: '8px 4px' }}>{row.assets}</td>
                                  <td style={{ textAlign: 'center', padding: '8px 4px' }}>{row.validity}</td>
                                  <td style={{ textAlign: 'center', padding: '8px 4px' }}>{row.fire_extinguishers}</td>
                                  <td style={{ textAlign: 'center', padding: '8px 4px' }}>{row.first_aid_count}</td>
                                  <td style={{ textAlign: 'center', padding: '8px 4px' }}>{row.medical_count}</td>
                                  <td style={{ textAlign: 'center', padding: '8px 4px' }}>{row.evse_count}</td>
                                  <td style={{ textAlign: 'center', padding: '8px 4px' }}>{row.aed_count}</td>
                                  <td style={{ textAlign: 'center', padding: '8px 4px' }}>{lgRcd3ph}</td>
                                  <td style={{ textAlign: 'center', padding: '8px 4px' }}>{mw}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        <div
                          style={{
                            marginTop: 12,
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Typography variant="body2">
                            {siteState.total_assets_selected || 0} selected of {siteState.total_assets_available || 0}
                          </Typography>
                        </div>
                      </>
                    )}
                  </div>
                </Collapse>
              </Paper>
            );
          })
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          disabled={selectedPayload.length === 0}
        >
          Generate Multi-Site Estimate
        </Button>
      </DialogActions>
    </Dialog>

    <CustomerNotesReviewDialog
      open={openCustomerNotesReview}
      onClose={() => setOpenCustomerNotesReview(false)}
      onConfirm={() => {
        setOpenCustomerNotesReview(false);
        generateEstimate();
      }}
      notes={customerNotes}
      bookingInstructions={bookingInstructions}
    />
    </>
    );
};

export default MultiSiteEstimateDialog;
